import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LoaderIcon, PlusIcon, SaveIcon, TrashIcon } from '@/components/icons';
import { UserName } from '@/components/UserName';
import { cortexApi } from '@/api/cortex.api';
import {
  Skill,
  SkillFile,
  SkillFileContent,
  SkillStatus,
  SkillVersion,
} from '@/proto/agentplatform/v1/skill_pb';
import { formatSkillTimestamp } from './skill.utils';

type TabType = 'overview' | 'content' | 'files';

const statusLabel: Record<SkillStatus, string> = {
  [SkillStatus.UNAVAILABLE]: '不可用',
  [SkillStatus.AVAILABLE]: '可用',
  [SkillStatus.UPLOADING]: '上传中',
};

const statusBadgeClass: Record<SkillStatus, string> = {
  [SkillStatus.UNAVAILABLE]: 'bg-destructive/10 text-destructive border-destructive/20',
  [SkillStatus.AVAILABLE]: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  [SkillStatus.UPLOADING]: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
};

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatFrontmatterValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map((v) => formatFrontmatterValue(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function frontmatterEntries(frontmatter?: Record<string, unknown>): Array<[string, string]> {
  if (!frontmatter) return [];
  return Object.entries(frontmatter).map(([k, v]) => [k, formatFrontmatterValue(v)]);
}

function yamlScalar(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'string') {
    return value === '' ? "''" : value;
  }
  return String(value);
}

function toYamlLines(value: unknown, indent = 0): string[] {
  const pad = ' '.repeat(indent);
  if (Array.isArray(value)) {
    if (value.length === 0) return [`${pad}[]`];
    return value.flatMap((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const lines = toYamlLines(item, indent + 2);
        if (lines.length === 0) return [`${pad}- {}`];
        const [first, ...rest] = lines;
        return [`${pad}- ${first.trimStart()}`, ...rest];
      }
      return [`${pad}- ${yamlScalar(item)}`];
    });
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return [`${pad}{}`];
    return entries.flatMap(([key, val]) => {
      if (val && typeof val === 'object') {
        const child = toYamlLines(val, indent + 2);
        return [`${pad}${key}:`, ...child];
      }
      return [`${pad}${key}: ${yamlScalar(val)}`];
    });
  }
  return [`${pad}${yamlScalar(value)}`];
}

function buildSkillMarkdown(content: string, frontmatter?: Record<string, unknown>): string {
  if (!frontmatter || Object.keys(frontmatter).length === 0) return content || '';
  const yamlBody = toYamlLines(frontmatter, 0).join('\n');
  const body = content || '';
  return `---\n${yamlBody}\n---\n\n${body}`;
}

export default function SkillManagementDetailPage() {
  const { uid: id = '' } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [skill, setSkill] = useState<Skill | null>(null);
  const [versions, setVersions] = useState<SkillVersion[]>([]);
  const [currentVersion, setCurrentVersion] = useState<SkillVersion | null>(null);
  const [files, setFiles] = useState<SkillFile[]>([]);

  const [tab, setTab] = useState<TabType>('overview');
  const [contentDraft, setContentDraft] = useState('');

  const [selectedFile, setSelectedFile] = useState<SkillFileContent | null>(null);
  const [fileDraft, setFileDraft] = useState('');
  const uploadRef = useRef<HTMLInputElement | null>(null);
  const uploadVersionZipRef = useRef<HTMLInputElement | null>(null);

  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [newVersionForm, setNewVersionForm] = useState({
    description: '',
    branch: '',
    createdBy: '',
    isStable: false,
  });

  const [newFileOpen, setNewFileOpen] = useState(false);
  const [newFileForm, setNewFileForm] = useState({ path: '', content: '' });

  const contentDirty = useMemo(
    () =>
      !!currentVersion &&
      contentDraft !==
        buildSkillMarkdown(
          currentVersion.content || '',
          currentVersion.frontmatter as Record<string, unknown>
        ),
    [currentVersion, contentDraft]
  );
  const fileDirty = useMemo(
    () => !!selectedFile && !selectedFile.file?.isBinary && fileDraft !== selectedFile.content,
    [selectedFile, fileDraft]
  );

  const reloadSkill = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const detail = await cortexApi.getSkill(id, true);
      setSkill(detail);
      const sorted = [...(detail.versions || [])].sort((a, b) => b.versionCode - a.versionCode);
      setVersions(sorted);
      const target = sorted.find((v) => v.isLatest) || sorted[0];
      if (target) {
        await switchVersion(target.versionCode);
      } else {
        setCurrentVersion(null);
        setFiles([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchVersion = async (versionCode: number) => {
    if (!id) return;
    setLoading(true);
    try {
      const version = await cortexApi.getSkillVersion(id, versionCode, 'content');
      const fileResp = await cortexApi.listSkillVersionFiles(id, versionCode);
      setCurrentVersion(version);
      setContentDraft(
        buildSkillMarkdown(version.content || '', version.frontmatter as Record<string, unknown>)
      );
      setFiles(fileResp.files || []);
      setSelectedFile(null);
      setTab((t) => t);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reloadSkill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveContentMutation = useMutation({
    mutationFn: async () => {
      if (!id || !currentVersion) return;
      const updated = await cortexApi.updateSkillVersionContent(
        id,
        currentVersion.versionCode,
        contentDraft
      );
      setCurrentVersion(updated);
      setContentDraft(
        buildSkillMarkdown(updated.content || '', updated.frontmatter as Record<string, unknown>)
      );
    },
  });

  const cloneMutation = useMutation({
    mutationFn: async () => {
      if (!id || !currentVersion) return;
      await cortexApi.cloneSkillVersion(id, currentVersion.versionCode);
      await reloadSkill();
    },
  });

  const setLatestMutation = useMutation({
    mutationFn: async () => {
      if (!id || !currentVersion) return;
      await cortexApi.promoteSkillVersion(id, currentVersion.versionCode, { isLatest: true });
      await reloadSkill();
    },
  });

  const toggleStableMutation = useMutation({
    mutationFn: async () => {
      if (!id || !currentVersion) return;
      await cortexApi.promoteSkillVersion(id, currentVersion.versionCode, {
        isStable: !currentVersion.isStable,
      });
      await reloadSkill();
    },
  });

  const deleteVersionMutation = useMutation({
    mutationFn: async (versionCode: number) => {
      if (!id) return;
      await cortexApi.deleteSkillVersion(id, versionCode);
      await reloadSkill();
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      await cortexApi.createSkillVersion(id, {
        description: newVersionForm.description || undefined,
        branch: newVersionForm.branch || undefined,
        createdBy: newVersionForm.createdBy || undefined,
        isStable: newVersionForm.isStable,
        content: contentDraft,
      });
      setNewVersionOpen(false);
      setNewVersionForm({ description: '', branch: '', createdBy: '', isStable: false });
      await reloadSkill();
    },
  });

  const importVersionZipMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!id) return;
      await cortexApi.importSkillZip(file, id);
      await reloadSkill();
    },
  });

  const saveFileMutation = useMutation({
    mutationFn: async () => {
      if (!id || !currentVersion || !selectedFile?.file) return;
      await cortexApi.putSkillVersionFile(id, currentVersion.versionCode, selectedFile.file.path, {
        content: fileDraft,
        encoding: 'utf-8',
      });
      const fresh = await cortexApi.getSkillVersionFile(
        id,
        currentVersion.versionCode,
        selectedFile.file.path
      );
      setSelectedFile(fresh);
      setFileDraft(fresh.content);
      const list = await cortexApi.listSkillVersionFiles(id, currentVersion.versionCode);
      setFiles(list.files || []);
    },
  });

  const createFileMutation = useMutation({
    mutationFn: async () => {
      if (!id || !currentVersion) return;
      await cortexApi.putSkillVersionFile(id, currentVersion.versionCode, newFileForm.path.trim(), {
        content: newFileForm.content,
        encoding: 'utf-8',
      });
      setNewFileOpen(false);
      setNewFileForm({ path: '', content: '' });
      const list = await cortexApi.listSkillVersionFiles(id, currentVersion.versionCode);
      setFiles(list.files || []);
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: async (filePath: string) => {
      if (!id || !currentVersion) return;
      await cortexApi.deleteSkillVersionFile(id, currentVersion.versionCode, filePath);
      if (selectedFile?.file?.path === filePath) {
        setSelectedFile(null);
      }
      const list = await cortexApi.listSkillVersionFiles(id, currentVersion.versionCode);
      setFiles(list.files || []);
    },
  });

  const onSelectFile = async (file: SkillFile) => {
    if (!id || !currentVersion) return;
    if (fileDirty && !window.confirm('当前文件未保存，确认切换？')) {
      return;
    }
    const content = await cortexApi.getSkillVersionFile(id, currentVersion.versionCode, file.path);
    setSelectedFile(content);
    setFileDraft(content.content);
  };

  const onUploadBinaryFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f || !id || !currentVersion) return;
    const bytes = new Uint8Array(await f.arrayBuffer());
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    const b64 = btoa(binary);
    await cortexApi.putSkillVersionFile(id, currentVersion.versionCode, f.name, {
      content: b64,
      encoding: 'base64',
    });
    const list = await cortexApi.listSkillVersionFiles(id, currentVersion.versionCode);
    setFiles(list.files || []);
    e.target.value = '';
  };

  const onExport = async () => {
    if (!id || !currentVersion) return;
    const resp = await cortexApi.exportSkillVersion(id, currentVersion.versionCode);
    const arrayBuffer = resp.data.buffer.slice(
      resp.data.byteOffset,
      resp.data.byteOffset + resp.data.byteLength
    ) as ArrayBuffer;
    const blob = new Blob([arrayBuffer], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${skill?.name || 'skill'}-v${currentVersion.versionCode}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onUploadVersionZip = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await importVersionZipMutation.mutateAsync(file);
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="max-w-[1500px] mx-auto px-6 py-8 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/plugins/skills/management')}
            >
              返回
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{skill?.name || 'Skill 详情'}</h1>
              {skill && (
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={statusBadgeClass[skill.status]}>
                    {statusLabel[skill.status]}
                  </Badge>
                  <span className="text-xs text-muted-foreground">#{skill.id}</span>
                  <span className="text-xs text-muted-foreground">
                    上传者：
                    <UserName userId={skill.userId} />
                  </span>
                </div>
              )}
              {skill?.description && (
                <p className="text-sm text-muted-foreground mt-1">{skill.description}</p>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={() => reloadSkill()}>
            刷新
          </Button>
        </div>

        {loading && !skill ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3 border rounded-lg p-3 bg-background space-y-2 max-h-[80vh] overflow-auto">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">版本</h3>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => uploadVersionZipRef.current?.click()}
                    disabled={importVersionZipMutation.isPending}
                  >
                    {importVersionZipMutation.isPending && (
                      <LoaderIcon className="h-3.5 w-3.5 mr-1" />
                    )}
                    上传新版本
                  </Button>
                  <input
                    ref={uploadVersionZipRef}
                    type="file"
                    accept=".zip"
                    className="hidden"
                    onChange={onUploadVersionZip}
                  />
                  <Button size="sm" variant="outline" onClick={() => setNewVersionOpen(true)}>
                    <PlusIcon className="h-3.5 w-3.5 mr-1" />
                    新版本
                  </Button>
                </div>
              </div>
              {versions.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-6">暂无版本</div>
              ) : (
                versions.map((v) => (
                  <button
                    key={v.versionCode}
                    className={`w-full text-left border rounded-md p-2 hover:bg-accent transition-colors ${currentVersion?.versionCode === v.versionCode ? 'border-primary bg-accent' : ''}`}
                    onClick={() => switchVersion(v.versionCode)}
                  >
                    <div className="flex items-center gap-1 flex-wrap text-sm">
                      <span className="font-medium">v{v.versionCode}</span>
                      {v.isLatest && <Badge variant="secondary">latest</Badge>}
                      {v.isStable && <Badge variant="secondary">stable</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{v.description || '—'}</div>
                  </button>
                ))
              )}
            </div>

            <div className="col-span-9 border rounded-lg p-4 bg-background min-h-[80vh] space-y-4">
              {!currentVersion ? (
                <div className="text-sm text-muted-foreground text-center py-12">
                  请选择左侧版本
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 flex-wrap border-b pb-3">
                    <div className="text-sm">当前版本：v{currentVersion.versionCode}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setLatestMutation.mutate()}
                        disabled={setLatestMutation.isPending || currentVersion.isLatest}
                      >
                        设为 latest
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleStableMutation.mutate()}
                        disabled={toggleStableMutation.isPending}
                      >
                        {currentVersion.isStable ? '取消 stable' : '设为 stable'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cloneMutation.mutate()}
                        disabled={cloneMutation.isPending}
                      >
                        克隆
                      </Button>
                      <Button size="sm" variant="outline" onClick={onExport}>
                        导出 zip
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (window.confirm(`确定删除 v${currentVersion.versionCode} 吗？`)) {
                            deleteVersionMutation.mutate(currentVersion.versionCode);
                          }
                        }}
                        disabled={deleteVersionMutation.isPending}
                      >
                        <TrashIcon className="h-3.5 w-3.5 mr-1" />
                        删除版本
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-1 border-b">
                    {[
                      ['overview', '概览'],
                      ['content', 'SKILL.md'],
                      ['files', '文件'],
                    ].map(([k, label]) => (
                      <button
                        key={k}
                        className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === k ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground'}`}
                        onClick={() => setTab(k as TabType)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {tab === 'overview' && (
                    <div className="space-y-4 text-sm">
                      <div>
                        <h4 className="font-semibold mb-2">Skill Meta</h4>
                        {frontmatterEntries(currentVersion.frontmatter).length > 0 ? (
                          <div className="border rounded-md overflow-hidden">
                            <table className="w-full text-sm">
                              <tbody>
                                {frontmatterEntries(currentVersion.frontmatter).map(([k, v]) => (
                                  <tr key={k} className="border-b last:border-b-0">
                                    <td className="w-52 px-3 py-2 bg-muted/30 font-mono text-xs text-muted-foreground align-top">
                                      {k}
                                    </td>
                                    <td className="px-3 py-2 break-all text-xs">{v || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-xs">无 Skill Meta</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-md border p-3 bg-muted/20">
                          <div className="text-xs text-muted-foreground">版本描述</div>
                          <div className="font-medium mt-1">
                            {currentVersion.description || '-'}
                          </div>
                        </div>
                        <div className="rounded-md border p-3 bg-muted/20">
                          <div className="text-xs text-muted-foreground">分支</div>
                          <div className="font-medium mt-1">{currentVersion.branch || '-'}</div>
                        </div>
                        <div className="rounded-md border p-3 bg-muted/20">
                          <div className="text-xs text-muted-foreground">文件数</div>
                          <div className="font-medium mt-1">{currentVersion.fileCount ?? 0}</div>
                        </div>
                        <div className="rounded-md border p-3 bg-muted/20">
                          <div className="text-xs text-muted-foreground">内容哈希</div>
                          <div className="font-mono text-xs mt-1 break-all">
                            {currentVersion.contentHash || '-'}
                          </div>
                        </div>
                        <div className="rounded-md border p-3 bg-muted/20">
                          <div className="text-xs text-muted-foreground">创建时间</div>
                          <div className="font-medium mt-1">
                            {formatSkillTimestamp(currentVersion.createdAt)}
                          </div>
                        </div>
                        <div className="rounded-md border p-3 bg-muted/20">
                          <div className="text-xs text-muted-foreground">更新时间</div>
                          <div className="font-medium mt-1">
                            {formatSkillTimestamp(currentVersion.updatedAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {tab === 'content' && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">直接编辑 SKILL.md</p>
                        <Button
                          size="sm"
                          disabled={!contentDirty || saveContentMutation.isPending}
                          onClick={() => saveContentMutation.mutate()}
                        >
                          {saveContentMutation.isPending && (
                            <LoaderIcon className="h-3.5 w-3.5 mr-1" />
                          )}
                          <SaveIcon className="h-3.5 w-3.5 mr-1" />
                          保存
                        </Button>
                      </div>
                      <textarea
                        className="w-full min-h-[520px] rounded-md border border-input px-3 py-2 text-xs font-mono"
                        value={contentDraft}
                        onChange={(e) => setContentDraft(e.target.value)}
                      />
                    </div>
                  )}

                  {tab === 'files' && (
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-4 border-r pr-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold">文件</h4>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setNewFileOpen(true)}>
                              <PlusIcon className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => uploadRef.current?.click()}
                            >
                              上传
                            </Button>
                            <input
                              ref={uploadRef}
                              type="file"
                              className="hidden"
                              onChange={onUploadBinaryFile}
                            />
                          </div>
                        </div>
                        {files.length === 0 ? (
                          <div className="text-xs text-muted-foreground py-6 text-center">
                            暂无文件
                          </div>
                        ) : (
                          files.map((f) => (
                            <button
                              key={f.path}
                              className={`w-full text-left border rounded p-2 text-xs hover:bg-accent transition-colors ${selectedFile?.file?.path === f.path ? 'border-primary bg-accent' : ''}`}
                              onClick={() => onSelectFile(f)}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-mono truncate">{f.path}</span>
                                <span
                                  className="text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`删除文件 ${f.path}？`)) {
                                      deleteFileMutation.mutate(f.path);
                                    }
                                  }}
                                >
                                  删除
                                </span>
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">
                                {formatBytes(f.size)} {f.isBinary ? '· 二进制' : ''}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                      <div className="col-span-8">
                        {!selectedFile ? (
                          <div className="text-sm text-muted-foreground text-center py-10">
                            请选择文件
                          </div>
                        ) : selectedFile.file?.isBinary ? (
                          <div className="text-xs text-muted-foreground border rounded p-4">
                            二进制文件，在线编辑暂不支持。
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-sm">{selectedFile.file?.path}</span>
                              <Button
                                size="sm"
                                disabled={!fileDirty || saveFileMutation.isPending}
                                onClick={() => saveFileMutation.mutate()}
                              >
                                <SaveIcon className="h-3.5 w-3.5 mr-1" />
                                保存
                              </Button>
                            </div>
                            <textarea
                              className="w-full min-h-[500px] rounded-md border border-input px-3 py-2 text-xs font-mono"
                              value={fileDraft}
                              onChange={(e) => setFileDraft(e.target.value)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={newVersionOpen} onOpenChange={setNewVersionOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>新建版本</AlertDialogTitle>
            <AlertDialogDescription>
              会基于当前编辑器中的 SKILL.md 创建新版本。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="描述"
              value={newVersionForm.description}
              onChange={(e) => setNewVersionForm((s) => ({ ...s, description: e.target.value }))}
            />
            <Input
              placeholder="分支"
              value={newVersionForm.branch}
              onChange={(e) => setNewVersionForm((s) => ({ ...s, branch: e.target.value }))}
            />
            <Input
              placeholder="创建人"
              value={newVersionForm.createdBy}
              onChange={(e) => setNewVersionForm((s) => ({ ...s, createdBy: e.target.value }))}
            />
            <label className="text-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={newVersionForm.isStable}
                onChange={(e) => setNewVersionForm((s) => ({ ...s, isStable: e.target.checked }))}
              />
              标记为 stable
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => createVersionMutation.mutate()}
              disabled={createVersionMutation.isPending}
            >
              创建
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={newFileOpen} onOpenChange={setNewFileOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>新建文件</AlertDialogTitle>
            <AlertDialogDescription>在当前版本中新增文本文件。</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="路径，如 scripts/run.py"
              value={newFileForm.path}
              onChange={(e) => setNewFileForm((s) => ({ ...s, path: e.target.value }))}
            />
            <textarea
              className="w-full min-h-[240px] rounded-md border border-input px-3 py-2 text-xs font-mono"
              placeholder="文件内容"
              value={newFileForm.content}
              onChange={(e) => setNewFileForm((s) => ({ ...s, content: e.target.value }))}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => createFileMutation.mutate()}
              disabled={!newFileForm.path.trim() || createFileMutation.isPending}
            >
              创建
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
